/* eslint-disable no-nested-ternary */
import React, { useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Bucket,
  Buckets,
  ColorOptionLabel,
  MiIcon,
} from "@miplatform/components";
import { TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import {
  IAttemptListItemResponseForWorkOrder,
  IAttemptListResponse,
  ICalendarWorkTemplateBase,
  IDateRangeISO,
  PopperPlacement,
} from "@miplatform/types";
import { getDate, getDayTitle } from "@miplatform/utils";
import { mdiChevronDown, mdiClose } from "@mdi/js";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  Grid,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";
import {
  MiWorkBoard,
  MiWorkBoardColumn,
  MiWorkBoardItem,
} from "../../components/MiWorkBoard";
import { useCalendarContext } from "../../providers";
import { OrderInfoCard as OrderInfoCardUntyped } from "../Orders/OrdersList/OrderInfoCard/OrderInfoCard";
import { NhcOrderCard } from "./NhcOrderCard";

const MIN_COLUMN_WIDTH = 300;

export const KanbanCalendar: React.FC<KanbanCalendarProps> = ({
  setSelectedOrder,
  orderReasons,
}) => {
  const {
    events,
    date,
    dateRange,
    calendarSet,
    calendarConfig,
    trayBuckets,
    isCalendarLoading,
    refreshData = () => null,
  } = useCalendarContext();

  const orders = events as IAttemptListResponse[];

  const orderTemplateOptions =
    calendarSet?.config.workTemplateBases.map((template) => ({
      label: template.name,
      value: template.id || template.name,
      color: template.color || theme.palette.grey.primary,
    })) ?? [];

  const workBaseRecords: Record<string, ICalendarWorkTemplateBase> =
    calendarSet?.config.workTemplateBases.reduce((acc, val) => {
      acc[val.name] = val;
      return acc;
    }, {} as Record<string, ICalendarWorkTemplateBase>) ?? {};

  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);

  const handleTemplateChange = (event: {
    target: { value: string | string[] };
  }) => {
    const { value } = event.target;
    if (Array.isArray(value) && value.includes("all-orders")) {
      setSelectedTemplates(orderTemplateOptions.map((option) => option.value));
    } else {
      setSelectedTemplates(
        typeof value === "string" ? value.split(",") : value
      );
    }
  };

  const handleClearSelection = () => {
    setSelectedTemplates([]);
  };

  const groupedOrders = useMemo(() => {
    const orderDays: Record<
      string,
      Record<string, IAttemptListResponse[]>
    > = {};

    const filteredOrders =
      selectedTemplates.length === 0
        ? orders
        : orders.filter(
            (order) =>
              order.assignment?.order?.orderTemplateId &&
              selectedTemplates.includes(
                order.assignment?.order?.orderTemplateId
              )
          );

    filteredOrders.forEach((order: IAttemptListResponse) => {
      const orderDay = getDayTitle(order.displayDate);
      const { assignment } = order;

      if (orderDays[orderDay] && assignment?.order?.orderTemplateName) {
        if (orderDays[orderDay][assignment.order?.orderTemplateName]) {
          orderDays[orderDay][assignment.order?.orderTemplateName].push(order);
        } else {
          orderDays[orderDay][assignment.order?.orderTemplateName] = [order];
        }
      } else {
        orderDays[orderDay] = {};
        if (assignment?.order?.orderTemplateName) {
          orderDays[orderDay][assignment.order?.orderTemplateName] = [order];
        }
      }
    });

    return orderDays;
  }, [orders, selectedTemplates]);

  const getDateRangeTitles = (range: IDateRangeISO) => {
    const dayTitles: string[] = [];
    if (!range.startDate) return dayTitles;
    const start = getDate(range.startDate);
    const end = range.endDate ? getDate(range.endDate) : start;
    let current = start.clone();
    while (current.isSameOrBefore(end, "day")) {
      dayTitles.push(getDayTitle(current));
      current = current.add(1, "days");
    }
    return dayTitles;
  };

  const columnHeaders = useMemo(() => {
    return getDateRangeTitles(dateRange);
  }, [dateRange, date, calendarConfig]);

  const { classes, theme } = useKanbanStyles();

  const getColumnSx = useMemo(() => {
    if (columnHeaders.length >= 3)
      return { xs: 12, sm: 6, md: 6, lg: 6, xl: 6 };
    if (columnHeaders.length === 2)
      return { xs: 12, sm: 12, md: 6, lg: 4, xl: 2 };
    return { xs: 12, sm: 6, md: 4, lg: 3, xl: 2 };
  }, [columnHeaders.length]);

  const handleBucketCardClick = (
    order: IAttemptListResponse | IAttemptListItemResponseForWorkOrder
  ) => {
    setSelectedOrder(order);
  };

  return (
    <Box className={classes.kanbanContainer}>
      <Box className={classes.boardSelectionContainer}>
        <FormControl sx={{ minWidth: 200 }} className={classes.boardSelector}>
          <Select
            multiple
            size="small"
            displayEmpty
            className={classes.boardSelector}
            value={selectedTemplates}
            onChange={handleTemplateChange}
            input={<OutlinedInput />}
            renderValue={(selected) => (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  width: "100%",
                  gap: 0.5,
                }}
              >
                {selected.length === 0 ? (
                  <Typography
                    variant={TYPOGRAPHY_VARIANTS.BODY2}
                    color={theme.palette.text.secondary}
                  >
                    Select Order Type
                  </Typography>
                ) : (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 0.5,
                        flex: 1,
                        pr: 0,
                      }}
                    >
                      {selected.length === orderTemplateOptions.length ? (
                        <Typography
                          variant={TYPOGRAPHY_VARIANTS.BODY2}
                          color={theme.palette.primary.main}
                          fontWeight={theme.typography.fontWeightBold}
                        >
                          All Orders
                        </Typography>
                      ) : (
                        selected.map((value, index) => {
                          const option = orderTemplateOptions.find(
                            (opt) => opt.value === value
                          );
                          return (
                            <React.Fragment key={value}>
                              <ColorOptionLabel
                                color={
                                  option?.color || theme.palette.grey.primary
                                }
                                label={option?.label || value}
                              />
                              {index < selected.length - 1 && (
                                <Typography
                                  variant={TYPOGRAPHY_VARIANTS.BODY2}
                                  sx={{ mx: 1 }}
                                >
                                  ,
                                </Typography>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </Box>
                    <Box
                      sx={{
                        opacity: 0.7,
                        "&:hover": { opacity: 1 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 20,
                        height: 20,
                        pointerEvents: "auto",
                      }}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleClearSelection();
                      }}
                      onMouseDown={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <MiIcon
                        path={mdiClose}
                        size={0.8}
                        color={theme.palette.text.secondary}
                      />
                    </Box>
                  </>
                )}
              </Box>
            )}
          >
            {orderTemplateOptions.map(({ color, label, value }) => (
              <MenuItem key={value} value={value}>
                <ColorOptionLabel color={color} label={label} />
              </MenuItem>
            ))}
            <MenuItem value="all-orders">
              <Typography
                variant={TYPOGRAPHY_VARIANTS.BODY1}
                color={theme.palette.primary.main}
                fontWeight={theme.typography.fontWeightBold}
              >
                All Orders
              </Typography>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
      <MiWorkBoard>
        <Box
          sx={{
            overflowX: "auto",
            overflowY: "auto",
            p: 3,
            height: "100%",
          }}
        >
          <Grid
            container
            spacing={1}
            sx={{
              minHeight: "100%",
              minWidth: `${
                columnHeaders.length * MIN_COLUMN_WIDTH +
                (columnHeaders.length - 1) * 4
              }px`,
              flexWrap: "nowrap",
            }}
          >
            {columnHeaders.map((date) => (
              <Grid
                size={12 / columnHeaders.length}
                key={date}
                sx={{
                  minHeight: "100%",
                  minWidth: MIN_COLUMN_WIDTH,
                  flexShrink: 0,
                }}
              >
                <MiWorkBoardColumn
                  title={date}
                  loading={isCalendarLoading}
                  count={
                    groupedOrders[date]
                      ? Object.values(groupedOrders[date]).reduce(
                          (acc: number, curr: IAttemptListResponse[]) =>
                            acc + curr.length,
                          0
                        )
                      : 0
                  }
                >
                  {groupedOrders[date] &&
                    Object.keys(groupedOrders[date]).map((templateName) => (
                      <OrderAccordion
                        key={templateName}
                        count={groupedOrders[date][templateName].length}
                        title={templateName}
                        backgroundColor={workBaseRecords[templateName]?.color}
                      >
                        <Grid container spacing={2}>
                          {groupedOrders[date][templateName].map(
                            (order: IAttemptListResponse) => {
                              const holdType = order?.assignment?.order
                                ?.holdCalendarId
                                ? calendarSet?.calendars?.[
                                    order?.assignment?.order?.holdCalendarId
                                  ]?.config?.name
                                : undefined;
                              return (
                                <Grid
                                  size={getColumnSx}
                                  key={order.id}
                                  sx={{ minWidth: 150 }}
                                >
                                  <MiWorkBoardItem key={order.id}>
                                    <NhcOrderCard
                                      data={order}
                                      onRefresh={refreshData}
                                      orderReasons={orderReasons}
                                      setSelectedOrder={setSelectedOrder}
                                      calendarSet={calendarSet}
                                      headerColor={
                                        workBaseRecords[templateName]?.color
                                      }
                                      holdType={holdType}
                                      popperPlacement={PopperPlacement.Right}
                                      popperComponent={
                                        <OrderInfoCard
                                          order={{
                                            home: order.home,
                                            ...order?.assignment?.order,
                                            notes:
                                              order?.assignment?.order
                                                ?.orderNotes,
                                          }}
                                          holdType={holdType}
                                        />
                                      }
                                    />
                                  </MiWorkBoardItem>
                                </Grid>
                              );
                            }
                          )}
                        </Grid>
                      </OrderAccordion>
                    ))}
                </MiWorkBoardColumn>
              </Grid>
            ))}
          </Grid>
        </Box>
      </MiWorkBoard>
      {trayBuckets.length > 0 && (
        <Box className={classes.bucketContainer}>
          <Buckets>
            {trayBuckets.map((bucket) => (
              <Bucket key={bucket.name} bucket={bucket} initialCollapsed>
                {(bucket.data || []).map(
                  (
                    order:
                      | IAttemptListResponse
                      | IAttemptListItemResponseForWorkOrder
                  ) => {
                    const holdType = order?.assignment?.order?.holdCalendarId
                      ? calendarSet?.calendars?.[
                          order.assignment.order.holdCalendarId
                        ]?.config?.name
                      : undefined;
                    return (
                      <MiWorkBoardItem
                        onClick={() => handleBucketCardClick(order)}
                        className={classes.bucketItem}
                        key={order.id}
                      >
                        <NhcOrderCard
                          data={order}
                          onRefresh={refreshData}
                          orderReasons={orderReasons}
                          setSelectedOrder={setSelectedOrder}
                          calendarSet={calendarSet}
                          headerColor={
                            order.assignment?.order?.orderTemplateColor ??
                            theme.palette.grey.primary
                          }
                          isInBucket
                          holdType={holdType}
                          popperPlacement={PopperPlacement.Top}
                          popperComponent={
                            <OrderInfoCard
                              order={{
                                home: order.home,
                                ...order?.assignment?.order,
                                notes: order?.assignment?.order?.orderNotes,
                              }}
                              holdType={holdType}
                            />
                          }
                        />
                      </MiWorkBoardItem>
                    );
                  }
                )}
              </Bucket>
            ))}
          </Buckets>
        </Box>
      )}
    </Box>
  );
};

const useKanbanStyles = makeStyles()((theme) => ({
  kanbanContainer: {
    padding: 0,
    overflow: "hidden",
    // backgroundColor: theme.palette.success.light,
    display: "flex",
    flexDirection: "column",
    flex: 1,
    position: "relative",
  },
  boardSelectionContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: theme.spacing(3, 0, 0, 3),
    gap: theme.spacing(1),
  },
  boardSelector: {
    "& .MuiSelect-select": {
      display: "flex",
      padding: theme.spacing(1, 2),
      alignItems: "center",
      borderRadius: theme.spacing(0.5),
      gap: theme.spacing(0.5),
      fontWeight: "600",
      fontSize: "500",
      color: theme.palette.common.black,
    },
    "& .MuiInputBase-root": {
      borderRadius: theme.spacing(0.5),
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "black ",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "black ",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "black ",
      borderWidth: "2px ",
    },
  },
  pinButton: {
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.65),
    border: `1px solid ${theme.palette.common.black}`,
    backgroundColor: theme.palette.background.default,
  },
  bucketContainer: {
    display: "flex",
    position: "sticky",
    bottom: 0,
  },
  bucketItem: {
    maxWidth: 170,
  },
}));

const useOrderAccordionStyles = makeStyles<{ backgroundColor: string }>()(
  (theme, { backgroundColor }) => ({
    accordionRoot: {
      backgroundColor: "transparent",
      boxShadow: "none",
      borderRadius: 0,
      "&:before": { display: "none" },
    },
    summaryRoot: {
      color: theme.palette.common.white,
      backgroundColor: backgroundColor ?? theme.palette.grey[500],
      fontSize: "0.75rem",
      fontWeight: 600,
      minHeight: theme.spacing(4),
      borderRadius: 4,
      border: `1px solid rgba(0,0,0, 0.2)`,
      "&.Mui-expanded": {
        minHeight: theme.spacing(4),
      },
    },
    summaryExpanded: {
      minHeight: theme.spacing(4),
    },
    summaryContent: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: 0,
      margin: 0,
      "&.Mui-expanded": {
        margin: 0,
      },
    },
    count: {
      backgroundColor: "rgba(0, 0, 0, 0.2)",
      color: "#fff",
      fontWeight: 500,
      borderRadius: "9999px",
      minWidth: 20,
      height: 20,
      padding: "0 6px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 12,
      lineHeight: 1,
    },
    detailsRoot: {
      padding: theme.spacing(1, 0),
      backgroundColor: "transparent",
    },
  })
);

interface OrderAccordionProps {
  children: React.ReactNode;
  title: string;
  backgroundColor: string;
  count: number;
}

const OrderAccordion: React.FC<OrderAccordionProps> = ({
  children,
  title,
  backgroundColor,
  count,
}) => {
  const { classes, theme } = useOrderAccordionStyles({ backgroundColor });
  return (
    <Accordion
      title="Order Details"
      classes={{ root: classes.accordionRoot }}
      defaultExpanded
    >
      <AccordionSummary
        classes={{
          root: classes.summaryRoot,
          content: classes.summaryContent,
          expanded: classes.summaryExpanded,
        }}
        expandIcon={
          <MiIcon path={mdiChevronDown} color={theme.palette.common.white} />
        }
      >
        <span className={classes.count}>{count}</span>
        <span>{title}</span>
      </AccordionSummary>
      <AccordionDetails classes={{ root: classes.detailsRoot }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

const OrderInfoCard = OrderInfoCardUntyped as React.FC<{
  order: any;
  fieldsConfig?: any;
  holdType?: string;
}>;
interface KanbanCalendarProps {
  setSelectedOrder: (
    order:
      | IAttemptListResponse
      | IAttemptListItemResponseForWorkOrder
      | undefined
  ) => void;
  orderReasons: {
    holdReasons: { value: string; label: string }[];
    rescheduleReasons: { value: string; label: string }[];
  };
}
